import React, { useEffect, useState } from 'react';
import { SiTrackPage } from '../../components/si-track-page';
import { SiTrackGrid } from '../../components/si-track-grid/si-track-grid';
import { Box, Button, Checkbox, Grid, Paper, Tooltip, Typography } from '@mui/material';
import { ReactHookForm } from '../../components/hook-form/react-hook-form.component';
import { useReactHookForm } from '../../hooks/useReactHookForm';
import { InvoiceExceptionReportColumns } from './invoice-exception-report-columns';
import { workorderReportApiKeyStore } from '../../api/workorder-report-api-key-store';
import { useLoadData } from '../../hooks/useLoadData';
import { ApiResponse } from '../../models/api-response';
import { UserPermissionName } from '../../models/user-permission-name';
import useEnhancedEffect from '@mui/material/utils/useEnhancedEffect';
import { GuardedComponent } from '../../components/guarded-component';
import { useNavigate } from 'react-router-dom';
import { useSaveData } from '../../hooks/useSaveData';
import { workOrdersApiKeyStore } from '../../api/workorders-api-key-store';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { LoadingIndicator } from '../../components/loading-indicator.component';
import { useDeleteData } from '../../hooks/useDeleteData';
import { GridCellProps } from '@progress/kendo-react-grid';
import { FormSelectDynamic } from '../../components/hook-form/form-select.dynamic.component';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { SiTrackGridWithGroupSelect } from '../../components/si-track-grid/si-track-grid-with-group-selection';
import { GridColumnConfig, GridPreference } from '../../models/gridCoumnConfig';
import { gridApiKeyStore } from '../../api/grid-api-key-store';
import { State } from '@progress/kendo-data-query';
import useDebounce from '../../hooks/use-debounce';
import { useAuthorize } from '../../hooks/use-authorize';

const styling = {
  paperStyle: { padding: '20px', borderRadius: '10px' },
};

const SELECTED_FIELD = 'isSelected';

interface CustomGridCellProps {
  field: string;
}

interface CellProps {
  inspectionData: any;
}

interface IResult {
  //   data: {
  customers: {
    customerName: string;
    qboCustomerSyncStatus: string;
    qboSyncedDate: string | null;
  }[];
  offices: {
    customerName: string;
    officeName: string;
    qboSubCustomerSyncStatus: string;
    qboSyncedDate: string | null;
  }[];
  invoices: {
    workOrderNumber: string;
    customerName: string | null;
    officeName: string | null;
    invoiceNo: string | null;
    invoiceAmount: string;
    invoiceDate: string | null;
    invoiceDueDate: string | null;
    qboInvoiceSyncStatus: string | null;
    invoiceCreatedOn: string | null;
    message: string;
    requestDate: string;
  }[];
  //   };
}

const InvoiceExceptionReport = () => {
  const form = useReactHookForm({
    defaultValues: {
      filter: {
        value: 'InvoiceNotGenerated',
        label: 'Invoice failed to generate',
      },
    },
  });

  const [statusChange, setStatus] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<string>('InvoiceNotGenerated');

  const { data, isLoading, isFetching, refetch } = useLoadData<ApiResponse<any[]>>(
    workorderReportApiKeyStore.getWorkOrderExceptionReports(!currentFilter ? 'InvoiceNotGenerated' : currentFilter)
  );

  const [result, setResult] = useState<any[] | null>(null);
  const [currentINvoiceExceptionIDs, setCurrentInvoiceExceptionIDs] = useState<number[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (data?.data) {
      setResult(data.data);
    }
  }, [data]);

  useEffect(() => {
    refetch();
  }, [statusChange]);

  const { isLoading: saveWorkordeLoading, mutateAsync } = useSaveData<any>(
    workOrdersApiKeyStore.qBOInvoiceGenerationRetry()
  );

  const { isLoading: isLodingdeleteInvoiceException, mutateAsync: deleteInvoiceException } = useSaveData<any>(
    workOrdersApiKeyStore.deleteInvoiceException()
  );

  const handleEditButtonClick = (inspectionData: any) => {
    navigate(`/workorders/edit/${inspectionData?.workOrderID}`, { state: { page: 'invoice exception report' } });
  };

  const handleRemoveButtonClick = () => {
    setIsOpen(true);
  };

  const handleRetryClick = (inspectionData: any) => {
    const data = {
      quickBookProcessingQueueID: inspectionData?.quickBookProcessingQueueID,
      workOrderID: inspectionData?.workOrderID,
    };
    mutateAsync({ data }).then(() => {
      refetch();
    });
  };

  const submitHanlder = async () => {
    await deleteInvoiceException({ data: currentINvoiceExceptionIDs }).then(() => {
      setIsOpen(false);
      setCurrentInvoiceExceptionIDs([]);
      refetch();
    });
  };

  const OptionCell = ({ inspectionData }: CellProps) => {
    return (
      <td style={{ cursor: 'pointer' }} onClick={() => { }} className="stickyCloumn">
        <div className="actionWidth">
          {inspectionData?.type === 'Retry' && (
            <GuardedComponent requiredPermissions={[UserPermissionName.RETRY_INVOICE_GENERATION]}>
              <span>
                <Button
                  type="button"
                  onClick={() => handleRetryClick(inspectionData)}
                  variant="contained"
                  color="primary"
                  className="kendoGridBtn"
                // disabled={isStatusLoading || isLoading || isFetching}
                >
                  Retry
                </Button>
              </span>
            </GuardedComponent>
          )}
          {inspectionData?.type === 'Edit' && (
            <>
              <GuardedComponent requiredPermissions={[UserPermissionName.EDIT_INVOICEAMOUNT]}>
                <Button
                  className="kendoGridBtn"
                  type="button"
                  onClick={() => handleEditButtonClick(inspectionData)}
                  variant="contained"
                  color="primary"
                >
                  Edit
                </Button>
              </GuardedComponent>
            </>
          )}
        </div>
      </td>
    );
  };

  const PriceAmountCell = ({ inspectionData }: CellProps) => {
    const price = inspectionData?.priceNumeric;

    const formattedAmount =
      price !== undefined && price !== null
        ? `$${parseFloat(price)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };
  const InvoiceAmountAmountCell = ({ inspectionData }: CellProps) => {
    const invoiceAmount = inspectionData?.invoiceAmountNumeric;

    const formattedAmount =
      invoiceAmount !== undefined && invoiceAmount !== null
        ? `$${parseFloat(invoiceAmount)?.toFixed(2)}` // Add dollar sign
        : '$0.00'; // Fallback to "0.00" if invoiceAmount is not available
    return (
      <td>
        <Typography component="span">{formattedAmount}</Typography>
      </td>
    );
  };

  const CustomGridCell = ({ field }: CustomGridCellProps): ((dataItem: any) => JSX.Element) | undefined => {
    if (field === 'option') {
      return (dataItem: any) => {
        return <OptionCell inspectionData={dataItem.dataItem} />;
      };
    }
    if (field === 'priceNumeric') {
      return (dataItem: any) => {
        return <PriceAmountCell inspectionData={dataItem.dataItem} />;
      };
    }
    if (field === 'invoiceAmountNumeric') {
      return (dataItem: any) => {
        return <InvoiceAmountAmountCell inspectionData={dataItem.dataItem} />;
      };
    }
  };

  const handleSelectedItem = (values: any[]) => {
    const invoiceIDs = values.map((item) => item.workOrderQBOInvoiceID);
    setCurrentInvoiceExceptionIDs(invoiceIDs);
  };

  const handleSubmit = () => { };

  const conditionalCellRender = (
    defaultRendering: React.ReactElement<HTMLTableCellElement> | null,
    props: GridCellProps
  ) => {
    if (props.field === SELECTED_FIELD && props.dataItem?.type !== 'Edit') {
      return (
        // <Tooltip title={<Typography variant="body1">You do not have permission for delete</Typography>}>
        <td>
          <Checkbox
            checked={true}
            icon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  border: '2px solid #000', // Outline color for unchecked state
                  backgroundColor: 'transparent',
                }}
              />
            }
            checkedIcon={
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: 'darkgray', // Filled color for checked state
                }}
              />
            }
          />
        </td>
        // </Tooltip>
      );
    } else {
      return defaultRendering;
    }
  };

  const changeExceptionType = (data: any) => {
    setCurrentFilter(data?.value);
    setCurrentInvoiceExceptionIDs([]);
    setStatus(!statusChange);
  };

  // -------------------------------------------------Grid Layout------------------------------

  const {
    isLoading: isLoadingGridLayout,
    data: gridLayouts,
    refetch: refetchGridLayout,
  } = useLoadData<ApiResponse<GridPreference>>(
    gridApiKeyStore.getGridLayout('InvoiceExceptionReport', 'InvoiceExceptionReport')
  );

  const { mutateAsync: mutateInvoiceExceptionAsync } = useSaveData<any>(gridApiKeyStore.saveGridLayOut());
  const [gridConfig, setGridConfig] = useState<{
    dataState: State | undefined;
    columns: any[] | undefined;
  }>();

  const [filteredInvoiceExceptionReports, setFilteredInvoiceExceptionReports] = useState<any[]>([]);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const filteredInvoiceExceptionReportList = data?.data?.filter((InvoiceExceptionReport: any) =>
      Object.values(InvoiceExceptionReport).some(
        (value) => value && value.toString().toLowerCase().includes(debouncedSearchTerm.toLowerCase())
      )
    );
    setFilteredInvoiceExceptionReports(filteredInvoiceExceptionReportList || []);
  }, [debouncedSearchTerm, data?.data]);

  const handleInputChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSaveLayout = (gridDataState: State, gridColumns: GridColumnConfig[]) => {
    const data = {
      filterJSON: JSON.stringify(gridDataState),
      columnJSON: JSON.stringify(gridColumns),
      gridName: 'InvoiceExceptionReport',
      pageName: 'InvoiceExceptionReport',
    };

    mutateInvoiceExceptionAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  const handleResetLayout = () => {
    const data = {
      filterJSON: null,
      columnJSON: null,
      gridName: 'InvoiceExceptionReport',
      pageName: 'InvoiceExceptionReport',
    };

    mutateInvoiceExceptionAsync({ data }).then(() => {
      refetchGridLayout();
    });
  };

  useEffect(() => {
    if (!gridLayouts?.data) return;

    try {
      const filterData = gridLayouts.data.filterJSON ? JSON.parse(gridLayouts.data.filterJSON) : null;

      const columnJson = gridLayouts.data.columnJSON ? JSON.parse(gridLayouts.data.columnJSON) : null;

      // if (filterData && columnJson) {
      setGridConfig({
        columns: columnJson,
        dataState: filterData,
      });
      // }
    } catch (error) {
      console.log('Error parsing grid layout JSON:', error);
    }
  }, [gridLayouts?.data]);

  // ----------------------------------------------------------------------------------------

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <SiTrackPage title="Invoice Exception Report">
      <ReactHookForm formName="Invoice Exception Report" onSubmit={handleSubmit} formContextProps={form}>
        <Grid container spacing={1}>
          {/* Filter and Remove Button Row */}
          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              alignItems: 'center', // Vertically center align
              justifyContent: 'space-between', // Spread filter and remove button to opposite sides
            }}
          >
            {/* Filter on the Left */}
            <Grid item xs={4}>
              <FormAutoCompleteSelectDynamic
                name="filter"
                label="Exception Type"
                dataItems={[
                  // { value: 'InvoiceAmountMissing', label: 'Invoice with zero price' },
                  { value: 'InvoiceNotGenerated', label: 'Invoice failed to generate' },
                  // { value: 'MismatchinInvoiceAmount', label: 'Mismatch with quickbooks invoice amount' },
                ]}
                optionsValueKey="value"
                optionsLabelKey="label"
                onChange={changeExceptionType}
                clearIcon={false}
              />
            </Grid>

            {/* Remove Button on the Right */}
            {/* <Grid item>
              <GuardedComponent requiredPermissions={[UserPermissionName.REMOVE_WORKORDER_INVOICE_EXCEPTION_REPORT]}>
                {currentINvoiceExceptionIDs?.length > 0 && currentFilter === 'InvoiceAmountMissing' && (
                  <Button
                    className="kendoGridBtn"
                    type="button"
                    variant="contained"
                    color="error"
                    style={{ marginLeft: 12, cursor: 'pointer' }}
                    onClick={() => handleRemoveButtonClick()}
                  >
                    Remove
                  </Button>
                )}
              </GuardedComponent>
            </Grid> */}
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Paper elevation={0} style={styling.paperStyle}>
              <SiTrackGridWithGroupSelect
                fileTitle='invoice_exception'
                columns={InvoiceExceptionReportColumns}
                dataItemKey="workOrderNumber"
                // dataItems={data?.data}
                actionPermissions={{
                  export: UserPermissionName.DOWNLOAD_GRID,
                }}
                // isDiableHeaderCell={true}
                isBusy={isLoading || isFetching || isLoadingGridLayout}
                customCell={(field) =>
                  CustomGridCell({
                    field,
                  })
                }
                onSelectedItems={handleSelectedItem}
                selectedField={undefined}
                // selectedField="isSelected"
                // conditionalCellRender={conditionalCellRender}
                // preventCheckAndUnCheckOnHeaderelectionChange={['workOrderNumber']}

                // -----------------Grid Layout-------
                dataItems={filteredInvoiceExceptionReports || []}
                reorderable
                handleSearch={handleInputChange}
                handleSaveLayout={handleSaveLayout}
                isSaveLayout={true}
                gridConfigFromApi={gridConfig}
                handleResetLayout={handleResetLayout}
              // -----------------Grid Layout-------
              />
            </Paper>
          </Grid>
        </Grid>
      </ReactHookForm>
      {isOpen && (
        <Dialog className="modalpopup" title={'CONFIRM REMOVE'} onClose={() => setIsOpen(false)}>
          <div>
            <div style={{ padding: '25px 50px' }}>
              <div>Are you sure you want to remove?</div>
              <LoadingIndicator isLoading={isLodingdeleteInvoiceException} />
            </div>
          </div>{' '}
          <DialogActionsBar>
            <div className=" spaceBetween">
              <button
                style={{ cursor: 'pointer' }}
                className="btn btn-sm btn-default btn-lightGrey"
                onClick={() => setIsOpen(false)}
                autoFocus
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-danger btn-delete"
                style={{
                  backgroundColor: isReadOnlyPermission ? '#d7dbdd' : '#ff8a8a',
                  color: isReadOnlyPermission ? 'black' : '#fff',
                  cursor: 'pointer'
                }}
                type="button"
                onClick={submitHanlder}
                disabled={isLodingdeleteInvoiceException || isReadOnlyPermission}
              >
                Remove
              </button>
            </div>
          </DialogActionsBar>
        </Dialog>
      )}
    </SiTrackPage>
  );
};

export default InvoiceExceptionReport;
